/**
 * Restore points (the Data tab's "Restore points" view, the project Home's part in More): the project's data saved at a
 * point in time, per store (PostgreSQL, MongoDB, Redis, SQLite, S3, Neo4j, Kafka: the project's topics, their messages and where its
 * consumer groups had read to). Points are
 * hourly (`auto`), saved by someone (`manual`), taken before a change (`pre-change`) or brought from the nightly backup
 * (`backup`). The words a person reads for each, from vfs-service's answers (DataService listRestorePoints).
 */
import { formatBytes, formatCount } from './dataModel';

export const STORE_NAMES = { postgres: 'PostgreSQL', mysql: 'MySQL', mongodb: 'MongoDB', redis: 'Redis', sqlite: 'SQLite', s3: 'S3', neo4j: 'Neo4j', kafka: 'Kafka' };

export const storeName = (id) => STORE_NAMES[id] || id || 'Store';

const KIND_LABELS = { auto: 'Hourly', manual: 'Saved', 'pre-change': 'Before a change', backup: 'From the nightly backup' };

/** "Hourly", "Saved", "Before a change", "From the nightly backup". */
export const pointKindLabel = (kind) => KIND_LABELS[kind] || 'Restore point';

const stripBefore = (text) => String(text || '').replace(/^Before:\s*/i, '').trim();

/**
 * The line under the kind: a saved point's name and who saved it ("Before the import · by Sara"), what change a
 * pre-change point came before ("deleted 3 rows in products"), the backup's date. Empty for an hourly point.
 */
export function pointDetail(point) {
  if (!point) { return ''; }
  if (point.kind === 'manual') {
    const who = point.by?.name || point.by?.email || '';
    return [point.name, who && `by ${who}`].filter(Boolean).join(' · ');
  }
  if (point.kind === 'pre-change') { return stripBefore(point.reason || point.name); }
  if (point.kind === 'backup') {
    const at = point.source?.backupCreatedAt || null;
    return at ? `Backup of ${absoluteTime(at)}` : '';
  }
  return '';
}

/** "17 Oct 2026, 14:00". */
export function absoluteTime(value) {
  const at = Date.parse(value || '');
  if (!Number.isFinite(at)) { return ''; }
  return new Date(at).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/** "Using 1.2 GB of 4 GB" (or only what is used when there is no maximum). */
export function budgetText(budget) {
  if (!budget || !Number.isFinite(Number(budget.usedBytes))) { return ''; }
  const used = formatBytes(budget.usedBytes);
  return Number(budget.maxBytes) > 0 ? `Using ${used} of ${formatBytes(budget.maxBytes)}` : `Using ${used}`;
}

/** The share of the budget used, 0-100, or null. */
export function budgetShare(budget) {
  const used = Number(budget?.usedBytes);
  const max = Number(budget?.maxBytes);
  if (!Number.isFinite(used) || !(max > 0)) { return null; }
  return Math.min(100, Math.max(0, Math.round((used / max) * 100)));
}

/** "Keeps the last 24 hourly points and 7 daily ones, within 10% of the data disk." */
export function policyText(policy) {
  if (!policy) { return ''; }
  const hourly = Number(policy.hourly) || 24;
  const daily = Number(policy.daily) || 7;
  const percent = Number(policy.budgetPercent);
  return `Keeps the last ${hourly} hourly points and ${daily} daily ones${percent > 0 ? `, within ${percent}% of the data disk` : ''}.`;
}

/** Why restore points cannot be used here, in a person's words (the answer's `reason`, `note` first when given). */
export function unavailableText(answer) {
  if (answer?.note) { return answer.note; }
  switch (answer?.reason) {
    case 'no-runner': return 'Restore points need your sandbox to be running: open a service of this project, then come back.';
    case 'old-runner': return 'Your sandbox runs an older version without restore points. Put it to sleep from the Sandbox page and open the project again (or ask your sandbox admin to Update it).';
    default: return 'Restore points are not available for this project right now.';
  }
}

/** "42 rows", "212 keys", "3 objects", "18 nodes", "120 messages" for one object of a point. */
export function objectCountText(object) {
  if (object?.count === undefined || object?.count === null) { return ''; }
  const n = Number(object.count);
  const words = {
    table: ['row', 'rows'], collection: ['document', 'documents'], keys: ['key', 'keys'], bucket: ['file', 'files'],
    label: ['node', 'nodes'], relationships: ['relationship', 'relationships'], topic: ['message', 'messages'],
    'consumer group': ['read position', 'read positions'],
  }[object.kind] || ['item', 'items'];
  return `${formatCount(n)} ${n === 1 ? words[0] : words[1]}`;
}

/** The stores of a point by name: "PostgreSQL, Redis". */
export const pointStoresText = (point) => (point?.stores || []).map((s) => storeName(s.store)).join(', ');

/** Whether what the person typed is the project's name (trimmed, case-insensitive), as vfs-service checks it. */
export function sameProjectName(typed, name) {
  const a = String(typed || '').trim().toLowerCase();
  const b = String(name || '').trim().toLowerCase();
  return Boolean(a) && a === b;
}

/** The points newest first (the server already sends them so; kept for safety when merging). */
export const newestFirst = (points = []) => [...points].sort((a, b) => (Date.parse(b.createdAt) || 0) - (Date.parse(a.createdAt) || 0));
